import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { getEventHomeworkApi, addEventHomeWorkApi, deleteEventHomeWorkApi, addHomeWorkAnswerApi, getHomeWorkAnswersApi } from '../../../api/events';

export const EventHomework = ({ eventId, communityId, isOwner }) => {
  const [homework, setHomework] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [activeHomework, setActiveHomework] = useState(null);
  const [showAnswersModal, setShowAnswersModal] = useState(false);

  useEffect(() => {
    if (eventId) fetchHomework();
  }, [eventId]);

  const fetchHomework = async () => {
    try {
      setLoading(true);
      const res = await getEventHomeworkApi({ eventId });
      setHomework(res.data || []);
    } catch (err) {
      console.warn('Failed to fetch homework', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this homework?')) return;
    try {
      await deleteEventHomeWorkApi(id);
      fetchHomework();
    } catch (err) {
      alert('Failed to delete homework.');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please provide a title');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('community', communityId);
      fd.append('event', eventId);
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      await addEventHomeWorkApi(fd);
      setShowAddForm(false);
      setFormData({ title: '', description: '' });
      fetchHomework();
    } catch (err) {
      alert('Failed to add homework.');
    } finally {
      setSubmitting(false);
    }
  };

  if (showAddForm) {
    return (
      <div className="bg-light p-3 rounded-4 border">
        <h6 className="fw-bold mb-3">Add New Homework</h6>
        <Form onSubmit={handleAddSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Homework title" 
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3}
              placeholder="Description or instructions" 
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="light" className="rounded-pill" onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting} className="rounded-pill px-4 cd-btn-orange border-0">
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Form>
      </div>
    );
  }

  return (
    <div>
      {activeHomework ? (
        <HomeworkAnswersView 
          onHide={() => setActiveHomework(null)} 
          homework={activeHomework} 
        />
      ) : (
        <>
          {isOwner && (
            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" className="rounded-pill px-4 cd-btn-orange border-0" onClick={() => setShowAddForm(true)}>
                <i className="bi bi-plus-lg me-1"></i> Add Homework
              </Button>
            </div>
          )}
          
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : homework.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-journal-text text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">No Homework Yet</h5>
              <p className="text-muted">No homework assignments have been added.</p>
            </div>
          ) : (
            <Row className="g-3">
              {homework.map((item) => (
                <Col md={6} key={item._id}>
                  <div 
                    className="card h-100 border-0 shadow-sm rounded-4 position-relative" 
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      if (e.target.closest('.delete-btn') || e.target.closest('a')) return;
                      setActiveHomework(item);
                    }}
                  >
                    {isOwner && (
                      <button 
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item._id);
                        }}
                        title="Delete Homework"
                        style={{ zIndex: 2 }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                    <div className="card-body text-center p-4">
                      <i className="bi bi-pencil-square text-primary" style={{ fontSize: '2.5rem' }}></i>
                      <h6 className="mt-3 mb-1 fw-bold text-truncate">{item.title}</h6>
                      <p className="text-muted small text-truncate-2 mb-2">{item.description}</p>
                      {item.createdAt && <small className="text-muted d-block mb-3">{new Date(item.createdAt).toLocaleDateString()}</small>}
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm rounded-pill px-3 mt-auto">
                          View Attachment
                        </a>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};

const HomeworkAnswersView = ({ onHide, homework }) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (homework?._id) {
      fetchAnswers();
    }
  }, [homework]);

  const fetchAnswers = async () => {
    try {
      setLoading(true);
      const res = await getHomeWorkAnswersApi(homework._id);
      setAnswers(res.answer || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnswer = async (e) => {
    e.preventDefault();
    if (!answerText.trim()) return;
    try {
      setSubmitting(true);
      await addHomeWorkAnswerApi({
        homework_id: homework._id,
        answer: answerText
      });
      setAnswerText('');
      fetchAnswers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit answer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-4 h-100 d-flex flex-column" style={{ minHeight: '400px' }}>
      
      <div className="d-flex align-items-center mb-4">
        <button className="btn btn-link text-dark p-0 me-3" onClick={onHide}>
          <i className="bi bi-chevron-left fs-5"></i>
        </button>
        <h5 className="fw-bold mb-0 text-truncate">{homework?.title}</h5>
      </div>

      <div className="flex-grow-1 overflow-auto pe-2 pb-4">
        <div className="bg-light rounded-3 p-3 mb-4">
          <p className="mb-0 fw-semibold text-break">{homework?.description || homework?.title}</p>
        </div>

            {loading ? (
              <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
            ) : answers.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-chat-dots fs-1 mb-2"></i>
                <p>No answers yet. Be the first to answer!</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {answers.map((ans) => {
                  const userName = ans.user_id?.full_name || 'User';
                  const initial = userName.charAt(0).toUpperCase();
                  return (
                    <div key={ans._id} className="bg-light rounded-3 p-3">
                      <div className="d-flex align-items-center mb-2">
                        {ans.user_id?.profile_picture ? (
                          <img 
                            src={ans.user_id.profile_picture} 
                            alt={userName} 
                            className="rounded-circle me-2 object-fit-cover" 
                            style={{ width: '35px', height: '35px' }} 
                          />
                        ) : (
                          <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2" style={{ width: '35px', height: '35px', fontSize: '1.2rem' }}>
                            {initial}
                          </div>
                        )}
                        <div>
                          <div className="fw-semibold lh-1">{userName}</div>
                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {ans.createdAt && new Date(ans.createdAt).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </small>
                        </div>
                      </div>
                      <div className="text-break" style={{ paddingLeft: '43px' }}>
                        {ans.answer}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-3 bg-white mt-auto rounded-bottom-4 border-top">
            <Form onSubmit={handleAddAnswer} className="w-100 d-flex align-items-center gap-2">
              <Form.Control 
                type="text" 
                placeholder="Write your answer..." 
                className="bg-light border-0 py-3 px-4 rounded-pill flex-grow-1"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                disabled={submitting}
              />
              <Button type="submit" variant="primary" className="rounded-circle d-flex align-items-center justify-content-center border-0" style={{ width: '50px', height: '50px', backgroundColor: '#2f349a' }} disabled={submitting || !answerText.trim()}>
                <i className="bi bi-send-fill fs-5"></i>
              </Button>
            </Form>
          </div>
    </div>
  );
};
